import type { StreamContribution } from '@wehunt/app-shell-web'
import {
  buildClubhouseRoutes,
  type CampfireSlots,
  type ChronicleSlots,
  type ClubhouseFeatures,
} from './routes'
import { useHuntStats } from './features/hunts/useHuntStats'
import { useMyGroups } from './features/teams/useMyGroups'

/**
 * Clubhouse stream's contribution to the WeHunt web shell.
 *
 * A factory because the overview renders chronicle UI inline: the composition
 * root (`apps/wehunt-web`) injects the chronicle slots so clubhouse never
 * imports the chronicle domain (RULE-002).
 *
 * The shell calls `useBadge` from inside its render tree, so the badge
 * hooks here run in the same context as the rest of the clubhouse app.
 * The stream owns its hooks end-to-end — apps/ never imports them.
 */
export function createClubhouseContribution(
  chronicle: ChronicleSlots,
  features: ClubhouseFeatures = {},
  campfire: CampfireSlots = {},
): StreamContribution {
  return {
    id: 'clubhouse',
    routes: (rootRoute) => buildClubhouseRoutes(rootRoute, chronicle, features, campfire),
    nav: {
      main: [
        { id: 'overview', label: 'Overview', icon: 'dashboard', href: '/',      order: 10 },
        {
          id: 'hunts',
          label: 'Hunts',
          icon: 'hunts',
          href: '/hunts',
          order: 20,
          useBadge: () => {
            const { data } = useHuntStats()
            return data === undefined ? undefined : data.upcomingHunts + data.pendingInvitations
          },
        },
        {
          id: 'teams',
          label: 'Teams',
          icon: 'group',
          href: '/teams',
          order: 30,
          useBadge: () => useMyGroups().data?.groups.length,
        },
      ],
      // Settings sits with clubhouse because clubhouse owns the /settings
      // route tree today. It moves to a dedicated host slot if/when the
      // settings page becomes cross-stream.
      account: [
        { id: 'settings', label: 'Settings', icon: 'settings', href: '/settings', order: 90 },
      ],
    },
  }
}
